import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import logo from '../../assets/pictures/logo.png';
import profileAssessmentIcon from '../../assets/Icon/Profile Assessment.png';
import preapplicationSupportIcon from '../../assets/Icon/Preapplication Support.png';
import applyUniversityIcon from '../../assets/Icon/Apply University.png';
import visaInterviewIcon from '../../assets/Icon/Visa & Interview Support.png';
import ticketTravelIcon from '../../assets/Icon/Ticket & Travel Support.png';
import accommodationIcon from '../../assets/Icon/Find Accommodation.png';
import educationLoanIcon from '../../assets/Icon/Education Loan.png';
import jobsAbroadIcon from '../../assets/Icon/Find Jobs Abroad.png';

const COUNTRIES = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 
  'Germany', 'France', 'Netherlands', 'Singapore', 
  'New Zealand', 'Ireland', 'Switzerland', 'Sweden'
];

const REFERRAL_SOURCES = [
  { value: 'representative', label: 'Representative' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'office_member', label: 'Office Member' },
  { value: 'friend', label: 'Friend/Referral' },
  { value: 'other', label: 'Other' }
];

const SERVICES = [
  { id: 'service-1', name: 'Profile Assessment', icon: '👤', color: 'bg-journey-profile' },
  { id: 'service-2', name: 'Pre-application Support', icon: '📄', color: 'bg-journey-preapp' },
  { id: 'service-3', name: 'Apply University', icon: '🎓', color: 'bg-journey-apply' },
  { id: 'service-4', name: 'Visa & Interview Support', icon: '✈️', color: 'bg-journey-visa' },
  { id: 'service-5', name: 'Ticket & Travel Support', icon: '🎫', color: 'bg-journey-ticket' },
  { id: 'service-6', name: 'Find Accommodation', icon: '🏠', color: 'bg-journey-accom' },
  { id: 'service-7', name: 'Education Loan', icon: '💰', color: 'bg-journey-loan' },
  { id: 'service-8', name: 'Find Jobs Abroad', icon: '💼', color: 'bg-journey-jobs' }
];

const ONBOARDING_SERVICES = [
  { id: 'service-1', name: 'Profile Assessment', icon: profileAssessmentIcon, color: 'bg-journey-profile' },
  { id: 'service-2', name: 'Pre-application Support', icon: preapplicationSupportIcon, color: 'bg-journey-preapp' },
  { id: 'service-3', name: 'Apply University', icon: applyUniversityIcon, color: 'bg-journey-apply' },
  { id: 'service-4', name: 'Visa & Interview Support', icon: visaInterviewIcon, color: 'bg-journey-visa' },
  { id: 'service-5', name: 'Ticket & Travel Support', icon: ticketTravelIcon, color: 'bg-journey-ticket' },
  { id: 'service-6', name: 'Find Accommodation', icon: accommodationIcon, color: 'bg-journey-accom' },
  { id: 'service-7', name: 'Education Loan', icon: educationLoanIcon, color: 'bg-journey-loan' },
  { id: 'service-8', name: 'Find Jobs Abroad', icon: jobsAbroadIcon, color: 'bg-journey-jobs' }
];

const SERVICE_ID_TO_SLUG = {
  'service-1': 'profile-assessment',
  'service-2': 'preapplication-support',
  'service-3': 'apply-university',
  'service-4': 'visa-interview',
  'service-5': 'ticket-travel',
  'service-6': 'accommodation',
  'service-7': 'education-loan',
  'service-8': 'jobs-abroad'
};

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    interestedCountries: [],
    selectedServices: [],
    phone: '',
    country: '',
    referralSource: '',
    representativeName: ''
  });

  const toggleCountry = (country) => {
    setFormData(prev => ({
      ...prev,
      interestedCountries: prev.interestedCountries.includes(country)
        ? prev.interestedCountries.filter(c => c !== country)
        : [...prev.interestedCountries, country]
    }));
  };

  const toggleService = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      selectedServices: prev.selectedServices.includes(serviceId)
        ? prev.selectedServices.filter(s => s !== serviceId)
        : [...prev.selectedServices, serviceId]
    }));
  };

  const handleNext = () => {
    if (step === 1 && formData.interestedCountries.length === 0) {
      toast.error('Please select at least one country');
      return;
    }
    if (step === 2 && formData.selectedServices.length === 0) {
      toast.error('Please select at least one service');
      return;
    }
    setStep(step + 1);
  };

  const handleComplete = async () => {
    if (!formData.phone || !formData.country) {
      toast.error('Please fill in all fields');
      return;
    }

    if (formData.referralSource === 'representative' && !formData.representativeName.trim()) {
      toast.error('Please enter the representative name');
      return;
    }

    setLoading(true);

    try {
      await api.post('/students/onboarding', {
        ...formData,
        interestedServices: formData.selectedServices
          .map((serviceId) => SERVICE_ID_TO_SLUG[serviceId])
          .filter(Boolean)
      });

      toast.success('Onboarding completed! Welcome to Fly8!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Onboarding failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReferralSourceChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      referralSource: value,
      representativeName: value === 'representative' ? prev.representativeName : ''
    }));
  };

  const renderStep1 = () => (
    <div className="space-y-5" data-testid="onboarding-step-1">
      <div className="mb-6 text-center">
        <h2 className="mb-2 text-2xl font-outfit font-bold text-slate-900 lg:text-[2rem]">Which countries interest you?</h2>
        <p className="text-slate-600">Select all countries you'd like to study in</p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {COUNTRIES.map((country) => (
          <div
            key={country}
            onClick={() => toggleCountry(country)}
            className={`cursor-pointer rounded-[20px] border p-3.5 transition-all hover:scale-[1.01] ${
              formData.interestedCountries.includes(country)
                ? 'border-[#A87DFA]/45 bg-[linear-gradient(135deg,rgba(168,125,250,0.18),rgba(168,125,250,0.08))] shadow-[0_12px_28px_rgba(168,125,250,0.16)]'
                : 'border-[#A87DFA]/16 bg-white/52 hover:border-[#A87DFA]/28'
            }`}
            data-testid={`country-option-${country}`}
          >
            <div className="flex items-center justify-between">
              <span className="font-medium text-slate-900">{country}</span>
              {formData.interestedCountries.includes(country) && (
                <Check className="h-5 w-5 text-[#6D49D4]" data-testid={`country-selected-${country}`} />
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={handleNext} size="lg" className="partner-primary-btn rounded-2xl" data-testid="onboarding-next-step-1">
          Continue
        </Button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-5" data-testid="onboarding-step-2">
      <div className="mb-6 text-center">
        <h2 className="mb-2 text-2xl font-outfit font-bold text-slate-900 lg:text-[2rem]">Which services do you need?</h2>
        <p className="text-slate-600">Select all services you want assistance with</p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {ONBOARDING_SERVICES.map((service) => (
          <div
            key={service.id}
            onClick={() => toggleService(service.id)}
            className={`cursor-pointer rounded-[22px] border p-4 transition-all hover:scale-[1.01] ${
              formData.selectedServices.includes(service.id)
                ? 'border-[#A87DFA]/45 bg-[linear-gradient(135deg,rgba(168,125,250,0.18),rgba(168,125,250,0.08))] shadow-[0_12px_28px_rgba(168,125,250,0.16)]'
                : 'border-[#A87DFA]/16 bg-white/52 hover:border-[#A87DFA]/28'
            }`}
            data-testid={`service-option-${service.id}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#A577F3]/28 bg-[#EBDDFF] shadow-[0_8px_18px_rgba(168,125,250,0.10)]">
                  <img src={service.icon} alt="" className="h-6 w-6 object-contain" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{service.name}</h3>
                </div>
              </div>
              {formData.selectedServices.includes(service.id) && (
                <Check className="h-5 w-5 flex-shrink-0 text-[#6D49D4]" data-testid={`service-selected-${service.id}`} />
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between pt-2">
        <Button onClick={() => setStep(1)} variant="outline" className="partner-secondary-btn rounded-2xl" data-testid="onboarding-back-step-2">
          Back
        </Button>
        <Button onClick={handleNext} size="lg" className="partner-primary-btn rounded-2xl" data-testid="onboarding-next-step-2">
          Continue
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-5" data-testid="onboarding-step-3">
      <div className="mb-6 text-center">
        <h2 className="mb-2 text-2xl font-outfit font-bold text-slate-900 lg:text-[2rem]">Complete your profile</h2>
        <p className="text-slate-600">Just a few more details to get started</p>
      </div>

      <div className="mx-auto max-w-md space-y-3.5">
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            type="tel"
            placeholder="+1 234 567 8900"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            data-testid="onboarding-phone-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="country">Current Country</Label>
          <Input
            id="country"
            placeholder="e.g., India, USA, UK"
            value={formData.country}
            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            data-testid="onboarding-country-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="referralSource">How did you hear about us?</Label>
          <select
            id="referralSource"
            value={formData.referralSource}
            onChange={(e) => handleReferralSourceChange(e.target.value)}
            className="flex h-11 w-full rounded-2xl border border-[#A87DFA]/20 bg-white/70 px-3 text-sm text-slate-900 shadow-[0_8px_20px_rgba(168,125,250,0.08)] outline-none transition focus:border-[#A87DFA]/40 focus:ring-2 focus:ring-[#A87DFA]/20"
            data-testid="onboarding-referral-source-select"
          >
            <option value="">Select an option</option>
            {REFERRAL_SOURCES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {formData.referralSource === 'representative' && (
          <div className="space-y-2">
            <Label htmlFor="representativeName">Representative Name</Label>
            <Input
              id="representativeName"
              placeholder="Enter representative name"
              value={formData.representativeName}
              onChange={(e) => setFormData({ ...formData, representativeName: e.target.value })}
              data-testid="onboarding-representative-name-input"
            />
          </div>
        )}

        <div className="mt-4 rounded-[20px] border border-[#A87DFA]/20 bg-[linear-gradient(135deg,rgba(168,125,250,0.14),rgba(255,255,255,0.52))] p-3.5">
          <h4 className="mb-2 font-semibold text-slate-900">Your Selections:</h4>
          <div className="space-y-1 text-sm text-slate-600">
            <p><span className="font-medium">Countries:</span> {formData.interestedCountries.join(', ')}</p>
            <p><span className="font-medium">Services:</span> {formData.selectedServices.length} selected</p>
            <p><span className="font-medium">Referral:</span> {REFERRAL_SOURCES.find((option) => option.value === formData.referralSource)?.label || 'Not selected'}</p>
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-md justify-between pt-2">
        <Button onClick={() => setStep(2)} variant="outline" className="partner-secondary-btn rounded-2xl" data-testid="onboarding-back-step-3">
          Back
        </Button>
        <Button onClick={handleComplete} size="lg" className="partner-primary-btn rounded-2xl" disabled={loading} data-testid="onboarding-complete-button">
          {loading ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Completing...</>
          ) : (
            'Complete Setup'
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="partner-shell min-h-screen bg-[#f7f4ff] px-4 py-4 lg:px-6 lg:py-5">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-7 text-center lg:mb-8">
          <div className="mb-3 inline-flex items-center gap-3 rounded-full border border-[#A87DFA]/20 bg-white/55 px-4 py-2 shadow-[0_12px_28px_rgba(168,125,250,0.12)] backdrop-blur-md">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#A87DFA]/20 bg-white/75 shadow-[0_10px_24px_rgba(168,125,250,0.12)]">
              <img src={logo} alt="Fly8" className="h-7 w-auto object-contain" />
            </div>
            <div className="text-left">
              <h1 className="text-3xl font-outfit font-bold text-slate-900">Fly8</h1>
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#8D68E8]">Onboarding</p>
            </div>
          </div>
          <p className="mx-auto max-w-2xl text-base text-slate-600 lg:text-lg">Welcome, {user?.firstName}! Let's set up your journey.</p>
        </div>

        {/* Progress Steps */}
        <div className="mb-7 flex items-center justify-center lg:mb-8" data-testid="onboarding-progress">
          {[1, 2, 3].map((num) => (
            <React.Fragment key={num}>
              <div className={`flex h-10 w-10 items-center justify-center rounded-full font-semibold ${
                step >= num ? 'bg-[linear-gradient(135deg,#8E66F1,#A87DFA)] text-white shadow-[0_10px_24px_rgba(168,125,250,0.22)]' : 'bg-white/70 text-slate-500 border border-[#A87DFA]/16'
              }`} data-testid={`progress-step-${num}`}>
                {num}
              </div>
              {num < 3 && (
                <div className={`h-1 w-14 rounded-full sm:w-20 ${step > num ? 'bg-[#A87DFA]' : 'bg-[#A87DFA]/16'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Content Card */}
        <div className="partner-card rounded-[28px] p-5 shadow-[0_18px_44px_rgba(168,125,250,0.16)] sm:p-6 lg:p-7">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
      </div>
    </div>
  );
}
